import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaCar, FaMapMarkerAlt, FaTools, FaHistory, FaEdit, FaArrowLeft, FaPrint, FaExternalLinkAlt, FaInfoCircle, FaCogs, FaSyncAlt, FaTrash } from 'react-icons/fa';
import axios from 'axios';

const BilProfil = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bilData, setBilData] = useState(null);
  const [serviceHistorik, setServiceHistorik] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingMot, setRefreshingMot] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [deletingId, setDeletingId] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeDraft, setResumeDraft] = useState(null);
  const [openingService, setOpeningService] = useState(false);

  useEffect(() => {
    fetchBilData();
    fetchServiceHistorik();
  }, [id]);

  const fetchBilData = async () => {
    try {
      console.log('Fetching bil data for ID:', id);
      const response = await axios.get(`/api/biler/profil/${id}`);
      console.log('Bil data response:', response.data);
      setBilData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bil data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      setLoading(false);
    }
  };

  const fetchServiceHistorik = async () => {
    try {
      const response = await axios.get(`/api/services/bil/${id}/historik`);
      setServiceHistorik(response.data);
    } catch (error) {
      console.error('Error fetching service history:', error);
      setServiceHistorik([]);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('da-DK');
  };

  const handleDeleteService = async (inspectionId) => {
    if (!inspectionId) return;
    const confirmed = window.confirm('Er du sikker på at du vil slette dette serviceeftersyn?');
    if (!confirmed) return;

    try {
      setDeletingId(inspectionId);
      await axios.delete(`/api/services/result/${inspectionId}`);
      setServiceHistorik(prev => prev.filter(item => item.id !== inspectionId));
    } catch (error) {
      console.error('Error deleting service:', error);
      alert('Kunne ikke slette serviceeftersynet');
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'I drift': return 'bg-green-100 text-green-800';
      case 'På værksted': return 'bg-yellow-100 text-yellow-800';
      case 'Udlånt': return 'bg-blue-100 text-blue-800';
      case 'Reserve': return 'bg-gray-100 text-gray-800';
      case 'Til salg': return 'bg-purple-100 text-purple-800';
      case 'På auktion': return 'bg-purple-100 text-purple-800';
      case 'Solgt': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const printProfil = () => {
    window.print();
  };

  const openMapsForAddress = (address) => {
    if (!address) return;
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(url, '_blank');
  };

  const refreshSynData = async () => {
    if (refreshingMot || !bil?.nummerplade) return;
    try {
      setRefreshingMot(true);
      const res = await axios.post('/api/biler/dmr-lookup', {
        nummerplade: bil.nummerplade,
        bil_id: bil.id
      });
      const data = res.data?.data;
      if (data && (data['mot_dato'] || data['mot_dato'] === '')) {
        setBilData(prev => {
          if (!prev || !prev.bil) return prev;
          return {
            ...prev,
            bil: {
              ...prev.bil,
              mot_dato: data['mot_dato'] ?? prev.bil.mot_dato,
              mot_resultat: data['mot_resultat'] ?? prev.bil.mot_resultat,
              mot_status: data['mot_status'] ?? prev.bil.mot_status,
              mot_kilometerstand: data['mot_kilometerstand'] ?? prev.bil.mot_kilometerstand
            }
          };
        });
      }
    } catch (error) {
      console.error('Kunne ikke opdatere synsdato:', error);
    } finally {
      setRefreshingMot(false);
    }
  };

  const handleStartNewService = () => {
    setShowResumeModal(false);
    setResumeDraft(null);
    navigate(`/services/selection?bil_id=${bil.id}&internt_nummer=${bil.internt_nummer}`);
  };

  const handleResumeLatestService = () => {
    if (!resumeDraft) return;
    setShowResumeModal(false);

    const categoryParam = resumeDraft.categoryIds?.length
      ? `&categories=${resumeDraft.categoryIds.join(',')}`
      : '';

    navigate(
      `/services/selection?bil_id=${bil.id}&internt_nummer=${bil.internt_nummer}${categoryParam}&resume_from=latest`
    );
  };

  const handleOpenServiceClick = async () => {
    if (!bil || openingService) return;
    try {
      setOpeningService(true);
      const res = await axios.get(`/api/services/bil/${bil.id}/latest`);
      if (res.data?.exists) {
        const categoryIds = Array.isArray(res.data.category_ids)
          ? res.data.category_ids.filter((id) => Number.isFinite(id))
          : [];

        setResumeDraft({
          updatedAt: res.data.completed_date || res.data.updated_at || '',
          categoryIds
        });
        setShowResumeModal(true);
      } else {
        navigate(`/services/selection?bil_id=${bil.id}&internt_nummer=${bil.internt_nummer}`);
      }
    } catch (error) {
      console.error('Kunne ikke hente seneste service:', error);
      navigate(`/services/selection?bil_id=${bil.id}&internt_nummer=${bil.internt_nummer}`);
    } finally {
      setOpeningService(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  if (!bilData) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Bil ikke fundet</h2>
          <button
            onClick={() => navigate('/biler')}
            className="mt-4 px-4 py-2 bg-falck-blue text-white rounded-lg hover:bg-blue-700"
          >
            Tilbage til biler
          </button>
        </div>
      </div>
    );
  }

  const { bil, laanehistorik } = bilData;
  const isBoatTrailer = ['båd trailer', 'både trailer', 'bådtrailer', 'bad trailer', 'boat trailer']
    .some((entry) => (bil?.vogn_type || '').toLowerCase() === entry);

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      {showResumeModal && resumeDraft && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4"
          onClick={() => setShowResumeModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-lg max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Fortsæt med seneste service?</h3>
            <p className="text-gray-700 mb-4">
              Der findes et tidligere serviceeftersyn{resumeDraft.updatedAt ? ' fra ' : ''}
              {resumeDraft.updatedAt ? new Date(resumeDraft.updatedAt).toLocaleDateString('da-DK') : ''}.
              Du kan starte fra det seneste data eller oprette et nyt skema.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleStartNewService}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
              >
                Start nyt skema
              </button>
              <button
                type="button"
                onClick={handleResumeLatestService}
                className="px-4 py-2 text-sm font-medium text-white bg-falck-blue rounded-md hover:bg-blue-700 transition-colors"
              >
                Fortsæt med seneste
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/biler')}
            className="p-2 text-gray-600 hover:text-gray-800"
          >
            <FaArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {bil.internt_nummer} - {bil.nummerplade || 'Ingen nummerplade'}
            </h1>
            <p className="text-gray-600">{bil.model} - {bil.vogn_type}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              if (bil?.internt_nummer) {
                const url = `/api/biler/offentlig-profile?internt_nummer=${encodeURIComponent(bil.internt_nummer)}`;
                window.open(url, '_blank');
              }
            }}
            className="bg-white text-gray-800 border border-gray-300 px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-50"
            title="Åbn offentlig profil"
          >
            <FaExternalLinkAlt /> <span className="hidden sm:inline">Offentlig profil</span>
          </button>
          <button
            onClick={printProfil}
            className="bg-gray-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-700"
          >
            <FaPrint /> <span className="hidden sm:inline">Print</span>
          </button>
          {localStorage.getItem('userRole') === 'admin' && (
            <button
              onClick={() => navigate(`/biler/edit/${bil.id}`)}
              className="bg-falck-blue text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
              title="Rediger"
            >
              <FaEdit /> <span className="hidden sm:inline">Rediger</span>
            </button>
          )}
        </div>
      </div>

      {/* Status Banner */}
      <div className={`mb-3 p-4 rounded-lg ${getStatusColor(bil.status)}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Status: {bil.status}</h3>
            <p className="text-sm opacity-75">Type: {bil.type}</p>
          </div>
          {bil.station_navn && (
            <div className="flex items-center gap-2">
              <FaMapMarkerAlt />
              <span>{bil.station_navn}</span>
            </div>
          )}
        </div>
      </div>

      {/* Primary actions */}
      <div className="flex justify-end mb-4 mt-1">
        <button
          onClick={handleOpenServiceClick}
          disabled={openingService}
          className="bg-falck-blue text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <FaTools /> Opret service
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <div className="overflow-x-auto no-scrollbar">
            <nav className="-mb-px flex space-x-6 sm:space-x-8 whitespace-nowrap px-1">
              <button
                onClick={() => setActiveTab('info')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'info'
                    ? 'border-falck-blue text-falck-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaCar className="inline mr-2" />
                Information
              </button>
              {bil.type === 'Lånebil' && (
                <button
                  onClick={() => setActiveTab('laanehistorik')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'laanehistorik'
                      ? 'border-falck-blue text-falck-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <FaHistory className="inline mr-2" />
                  Lånehistorik
                </button>
              )}
              <button
                onClick={() => setActiveTab('service')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'service'
                    ? 'border-falck-blue text-falck-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaTools className="inline mr-2" />
                Service Historik
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
          {activeTab === 'info' && (
            <div className="px-2 sm:px-4 md:px-6 py-3 sm:py-5 space-y-6">
              {/* Generelle oplysninger */}
              <section className="border border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50 dark:bg-gray-900/60 p-4 sm:p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <FaCar className="text-falck-blue" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Generelle oplysninger</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <InfoRow label="Nummerplade" value={bil.nummerplade || 'Ikke registreret'} />
                  <InfoRow label="Vogn nr." value={bil.internt_nummer || 'Ukendt'} />
                  <InfoRow label="Vogn type" value={bil.vogn_type || 'Ukendt'} />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">Station</p>
                    {bil.station_navn ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/stationer/profil/${bil.station_id}`)}
                        className="mt-1 text-sm font-semibold text-falck-blue hover:underline flex items-center gap-1"
                      >
                        {bil.station_navn}
                      </button>
                    ) : (
                      <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">Ikke tilknyttet</p>
                    )}
                  </div>
                  <InfoRow label="Stelnummer" value={bil.stelnummer || 'Ikke registreret'} />
                </div>
              </section>

                {isBoatTrailer && (
                  <section className="border border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50 dark:bg-gray-900/60 p-4 sm:p-6 shadow-sm">
                    <div className="flex items-center gap-2 mb-4">
                      <FaInfoCircle className="text-falck-blue" />
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Båd oplysninger</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <InfoRow label="Båd model" value={bil.boat_model || 'Tilføjes ved service'} />
                      <InfoRow label="Motor" value={bil.boat_motor || 'Tilføjes ved service'} />
                      <InfoRow label="Motorkode" value={bil.boat_motor_code || 'Tilføjes ved service'} />
                      <InfoRow label="Noter" value={bil.boat_notes || ''} />
                    </div>
                  </section>
                )}

                {/* Status og kategori */}
                <section className="border border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50 dark:bg-gray-900/60 p-4 sm:p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <FaInfoCircle className="text-falck-blue" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Status &amp; kategori</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">Status</p>
                      <span className={`inline-flex mt-1 px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(bil.status)}`}>
                        {bil.status || 'Ukendt'}
                      </span>
                    </div>
                    <InfoRow label="Type" value={bil.type || 'Ukendt'} />
                  </div>
                </section>

                {/* Teknisk information */}
                <section className="border border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50 dark:bg-gray-900/60 p-4 sm:p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-4">
                    <FaCogs className="text-falck-blue" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Teknisk information</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoRow label="Mærke" value={bil["mærke"] || bil.maerke || 'Ikke angivet'} />
                    <InfoRow label="Model" value={bil.model || 'Ikke angivet'} />
                    <InfoRow label="Første registreringsdato" value={bil["første_registreringsdato"] ? formatDate(bil["første_registreringsdato"]) : 'Ikke registreret'} />
                    <InfoRow label="Art" value={bil.art || 'Ikke angivet'} />
                    <InfoRow
                      label="Synsdato"
                      value={bil.mot_dato ? formatDate(bil.mot_dato) : 'Ikke registreret'}
                      action={bil.nummerplade ? (
                        <button
                          type="button"
                          onClick={refreshSynData}
                          className="inline-flex items-center justify-center text-falck-blue hover:text-blue-700 disabled:opacity-40"
                          disabled={refreshingMot}
                          title="Opdater synsdato"
                        >
                          <FaSyncAlt className={refreshingMot ? 'animate-spin' : ''} />
                        </button>
                      ) : null}
                    />
                    <InfoRow label="Kilometerstand" value={bil.mot_kilometerstand ? `${bil.mot_kilometerstand} km` : 'Ikke registreret'} />
                  </div>
                </section>
            </div>
          )}

        {activeTab === 'laanehistorik' && bil.type === 'Lånebil' && (
          <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
            <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">Lånehistorik</h2>
            {laanehistorik.length > 0 ? (
              <div className="scroll-fade-x"><div className="scroll-area">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Periode
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Station
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Erstatter
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                    {laanehistorik.map((historik, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(historik.startdato)}
                          {historik.slutdato && (
                            <span> - {formatDate(historik.slutdato)}</span>
                          )}
                          {!historik.slutdato && (
                            <span className="text-blue-600 dark:text-blue-300 font-medium"> (Aktiv)</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {historik.station_navn}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {historik.erstattet_bil_internt_nummer || 'Ingen'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div></div>
            ) : (
              <p className="text-gray-500">Ingen lånehistorik fundet</p>
            )}
          </div>
        )}

        {activeTab === 'service' && (
          <div className="p-6">
            <div className="flex items-center mb-4">
              <h2 className="text-xl font-semibold">Service Historik</h2>
            </div>
            
            {serviceHistorik.length > 0 ? (
              <div className="scroll-fade-x"><div className="scroll-area">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dato
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kategori
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Noter
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Skema
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Handlinger
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                    {serviceHistorik.map((inspection, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {formatDate(inspection.completed_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {inspection.category_name || 'Ukendt kategori'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/70 dark:text-green-200">
                            Gennemført
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {inspection.notes || 'Ingen noter'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                          <a
                            href={`/api/services/result/${inspection.id}/pdf?format=html`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-falck-blue hover:underline"
                          >
                            Åbn HTML
                          </a>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-gray-100">
                          <button
                            onClick={() => handleDeleteService(inspection.id)}
                            className="inline-flex items-center gap-2 px-3 py-1 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 rounded hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                            disabled={deletingId === inspection.id}
                          >
                            <FaTrash />
                            <span className="hidden sm:inline">Slet</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div></div>
            ) : (
              <p className="text-gray-500">Ingen service historik fundet</p>
            )}

            {/* Fjernet separat Serviceskemaer-sektion for at undgå dublering */}
          </div>
        )}
      </div>
    </div>
  );
};

export default BilProfil;

const InfoRow = ({ label, value, action }) => (
  <div>
    <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">{label}</p>
    <div className="mt-1 flex flex-wrap items-center gap-2">
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 break-words">
        {value ?? 'Ikke angivet'}
      </p>
      {action}
    </div>
  </div>
);
